<script setup lang="ts">
import { computed, defineEmits, ref } from 'vue'
import { type FormInst, type FormRules, NForm, NFormItem, useMessage } from 'naive-ui'
import { t } from '@/locales'
import { checkAccount, updatePassword } from '@/api/auth'
import { validatePassword } from '@/utils/validators'
import { useAuthStore, useUserStore } from '@/store'

const props = defineProps<{
  show: boolean
  info?: string
}>()

const emit = defineEmits(['onClose'])

const showModal = ref(props.show)
const message = useMessage()
const userStore = useUserStore()
const authStore = useAuthStore()
const userInfo = computed(() => userStore.userInfo)

const modelRef = ref({
  email: userInfo.value.email || null,
  currentPassword: null,
  password: null,
  confirmPassword: null,
})

const formRef = ref<FormInst | null>(null)
const loading = ref<boolean>(false)

const validateConfirmPassword = (rule: any, value: string) => {
  if (value && value !== modelRef.value.password)
    return new Error(t('auth.validation.password_mismatch'))
  return true
}

const rulesRef: FormRules = {
  currentPassword: [
    {
      required: true,
      message: t('auth.validation.required_current_password'),
      trigger: ['blur', 'input', 'change'],
    },
  ],
  password: [
    {
      required: true,
      message: t('auth.validation.required_new_password'),
      trigger: ['blur', 'input', 'change'],
    },
    {
      validator: validatePassword,
      trigger: ['blur', 'input', 'change'],
    },
  ],
  confirmPassword: [
    {
      required: true,
      message: t('auth.validation.required_confirm'),
      trigger: ['blur', 'input', 'change'],
    },
    {
      validator: validateConfirmPassword,
      trigger: ['blur', 'input', 'change'],
    },
  ],
}

const closeModal = (reload: boolean) => {
  showModal.value = false
  emit('onClose', reload)
}

const handleCancel = () => {
  closeModal(false)
}

const checkVerifyAccount = async () => {
  try {
    if (!modelRef.value.email || !modelRef.value.currentPassword)
      return
    loading.value = true
    const checkParams = {
      email: modelRef.value.email,
      password: modelRef.value.currentPassword,
      confirmPassword: modelRef.value.currentPassword,
      token: authStore.token,
    }

    const { data } = await checkAccount(checkParams)

    if (data?.code === 'API000')
      await onChangePassword()
  }
  catch (e: any) {
  }
  finally {
    loading.value = false
  }
}

const onChangePassword = async () => {
  try {
    const params = {
      id: userInfo.value.userId,
      currentPassword: modelRef.value.currentPassword,
      password: modelRef.value.password,
      confirmPassword: modelRef.value.confirmPassword,
    }
    const { data } = await updatePassword(params)
    if (data?.code === 'API000') {
      message.success(t('auth.changePasswordSuccess'))
      closeModal(true)
    }
    else {
      message.error(t('auth.changePasswordFailed'))
    }
  }
  catch (e: any) {
  }
}

const handleChangePassword = () => {
  formRef.value?.validate().then(async () => {
    await checkVerifyAccount()
  }).catch((e: any) => {
    console.log('e', e)
  }).finally(() => {})
}
</script>

<template>
  <AppModal
    v-model:show="showModal"
    preset="card"
    transform-origin="center"
    style="width: 520px"
    class="!rounded-2xl"
    content-class="!px-4 !py-0"
    header-class="!p-4"
    footer-class="!p-4 !w-full !pt-3"
    :mask-closable="false"
    @after-leave="handleCancel"
    @esc="handleCancel"
  >
    <template #header>
      <div class="text-center text-2xl font-semibold text-gray-900 dark:text-neutral-100">
        {{ $t('auth.change_password') }}
      </div>
    </template>

    <div class="w-full">
      <NForm ref="formRef" :model="modelRef" :rules="rulesRef" @keydown.enter.prevent="handleChangePassword">
        <NFormItem :label="t('auth.current_password')" path="currentPassword">
          <AppInput
            v-model:value="modelRef.currentPassword"
            type="password"
            placeholder="******"
            show-password-on="mousedown"
            size="medium"
            class=""
          />
        </NFormItem>
        <NFormItem :label="t('auth.new_password')" path="password">
          <AppInput
            v-model:value="modelRef.password"
            type="password"
            placeholder="******"
            show-password-on="mousedown"
            size="medium"
            class=""
          />
        </NFormItem>
        <NFormItem :label="t('auth.confirm_password')" path="confirmPassword">
          <AppInput
            v-model:value="modelRef.confirmPassword"
            type="password"
            placeholder="******"
            show-password-on="mousedown"
            size="medium"
            class=""
          />
        </NFormItem>
      </NForm>
    </div>

    <template #footer>
      <div class="w-full flex gap-3">
        <AppButton
          class=" flex-1"
          size="medium"
          @click="handleCancel"
        >
          {{ $t('common.cancel') }}
        </AppButton>
        <AppButton
          type="primary"
          class="flex-1"
          size="medium"
          :loading="loading"
          @click="handleChangePassword"
        >
          {{ $t('common.save') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<style scoped>

</style>
